// Shared line-items editor used by Quotes + Purchase Orders. Each row is
// { description, qty, unitPriceCents }; total recalcs live.
import type { ReactNode } from 'react'

export interface LineItemDraft { description: string; qty: number; unitPriceCents: number }

export function lineItemsTotal(items: LineItemDraft[]): number {
  return items.reduce((acc, it) => acc + it.qty * it.unitPriceCents, 0)
}

export default function LineItemEditor({
  items, onChange, footer,
}: {
  items: LineItemDraft[]
  onChange: (next: LineItemDraft[]) => void
  footer?: ReactNode
}) {
  function update(i: number, patch: Partial<LineItemDraft>) {
    onChange(items.map((it, ix) => ix === i ? { ...it, ...patch } : it))
  }
  function add() { onChange([...items, { description: '', qty: 1, unitPriceCents: 0 }]) }
  function remove(i: number) { onChange(items.filter((_, ix) => ix !== i)) }

  return (
    <div>
      <div className="rounded border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface text-[10px] uppercase tracking-wider text-ink-3">
            <tr>
              <th className="text-left px-3 py-1.5 font-bold">Description</th>
              <th className="text-right px-3 py-1.5 font-bold w-20">Qty</th>
              <th className="text-right px-3 py-1.5 font-bold w-32">Unit (cents)</th>
              <th className="text-right px-3 py-1.5 font-bold w-28">Line</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-4 text-center text-ink-3 text-xs">No line items yet.</td></tr>
            )}
            {items.map((it, i) => (
              <tr key={i}>
                <td className="px-3 py-1.5">
                  <input value={it.description} onChange={(e) => update(i, { description: e.target.value })}
                    placeholder="Description" className="w-full px-2 py-1 border border-border rounded text-sm focus:outline-none focus:border-gold" />
                </td>
                <td className="px-3 py-1.5">
                  <input type="number" min={1} value={it.qty} onChange={(e) => update(i, { qty: Math.max(1, parseInt(e.target.value) || 1) })}
                    className="w-full px-2 py-1 border border-border rounded text-sm focus:outline-none focus:border-gold text-right" />
                </td>
                <td className="px-3 py-1.5">
                  <input type="number" min={0} step={1} value={it.unitPriceCents} onChange={(e) => update(i, { unitPriceCents: Math.max(0, parseInt(e.target.value) || 0) })}
                    className="w-full px-2 py-1 border border-border rounded text-sm focus:outline-none focus:border-gold text-right" />
                </td>
                <td className="px-3 py-1.5 text-right font-semibold text-ink">${((it.qty * it.unitPriceCents) / 100).toFixed(2)}</td>
                <td className="px-3 py-1.5 text-right"><button onClick={() => remove(i)} className="text-ink-3 hover:text-red-600 text-sm" title="Remove">×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-2">
        <button onClick={add} className="text-xs text-gold-dark hover:underline">+ Add line item</button>
        {footer}
      </div>
    </div>
  )
}
